import React from 'react';
import { View, FlatList } from 'react-native';
import { useResponsive } from '@/hooks/common/useResponsive';
import { TicketTypes, MaintenanceEmptyState } from './MaintenanceEmptyState';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { DesktopGrid } from '@/components/residents/desktop/DesktopGrid';
import { EmptyStateView } from '@/components/common/EmptyStateView';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';

interface MaintenanceListProps<T = any> {
  loading: boolean;
  error?: unknown;
  items: T[] | null | undefined;
  emptyType: TicketTypes;
  renderItem: (item: T, index: number) => React.ReactNode;
  emptyOverride?: { title: string; message: string };
  // Mobile-only enhancements
  listHeaderComponent?: React.ReactElement | null;
  listFooterComponent?: React.ReactElement | null;
  refreshing?: boolean;
  onRefresh?: () => void;
}

export const MaintenanceList = <T,>({ loading, error, items, emptyType, renderItem, emptyOverride, listHeaderComponent, listFooterComponent, refreshing, onRefresh }: MaintenanceListProps<T>) => {
  const { layoutMode } = useResponsive();
  const isDesktop = layoutMode === 'desktop';

  if (loading) {
    return (
      <LoadingSpinner 
        message={'Retrieving Maintenance Information. Please wait, this may take a moment.'}
        size="large" 
        showMessage
      />
    );
  }

  if (error) {
    return (
      <EmptyStateView
        icon={<RESIDENTS_SVG_COMPONENTS.EMPTY_CONVERSATION width={64} height={64} />}
        title={emptyOverride?.title || 'Unable to load service requests'}
        message={emptyOverride?.message || 'Please refresh the page or try again later.'}
      />
    );
  }

  if (items && items.length === 0) {
    if (emptyOverride) {
      return (
        <EmptyStateView
          icon={<RESIDENTS_SVG_COMPONENTS.EMPTY_CONVERSATION width={64} height={64} />}
          title={emptyOverride.title}
          message={emptyOverride.message}
        />
      );
    }
    return <MaintenanceEmptyState ticketType={emptyType} />;
  }

  if (isDesktop) {
    return <DesktopGrid>{items?.map(renderItem)}</DesktopGrid>;
  }

  // Mobile: use FlatList for better performance on large lists
  return (
    <FlatList
      data={(items || []) as any}
      keyExtractor={(item: any, index: number) => String(item?.maintenanceRequestId ?? index)}
      ListHeaderComponent={listHeaderComponent as any}
      ListFooterComponent={listFooterComponent as any}
      refreshing={Boolean(refreshing)}
      onRefresh={onRefresh}
      renderItem={({ item, index }) => {
        const node = renderItem(item as any, index);
        return (node as any) ?? null;
      }}
    />
  );
};


